import React, {useEffect, useState, useMemo} from 'react';
import './index.css';
import _get from 'lodash/get';
import {
  getDetectionLanguages,
  transformSubOptionToOption,
} from '../../../../../utils/skincareUtils';
import _isEmpty from 'lodash/isEmpty';
import skincareProDao from '../../../../../dao/skincareProDao';
import {detectionMap, detectionHdMap, skinTypeHdMap} from './setting';
import langCode from '../../../../../utils/langUtils';
import PreviewToolBar from './components/PreviewToolBar';
import {deviceOptions} from '../../config/utils/optionsUtils';
import storeUtils from '../../../../../utils/storeUtils';
import {useSelector} from 'react-redux';
import smbUtils from '../../../../../utils/smbUtils';
import classnames from 'classnames';
import DetectionPreviewModal from './components/DetectionPreviewModal';
import {Modal} from 'react-bootstrap';
import useWindowHeight from '../../../../../components/hook/useWindowHeight';
import useWindowWidth from '../../../../../components/hook/useWindowWidth';

export default function SkincareDetectionPreview({
  config,
  commonData = {},
  isV2Above = true,
  isHd = false,
  isSupportSubOptions = false,
}) {
  const {
    isShowSkinAge,
    isShowSkinType,
    isShowSkinHealth,
    sequenceOfCategory,
    skincareRecommendMode,
    detectionDescriptionContent,
    recommendCategory,
    buttonText,
  } = config;
  const auth = useSelector(state => state.components.auth.accountSummary);
  const {customerId} = smbUtils.customerInfo(auth);

  const transformedSequenceOfCategory = isSupportSubOptions
    ? transformSubOptionToOption(sequenceOfCategory)
    : sequenceOfCategory;

  const [activeDetection, setActiveDetection] = useState(
    isHd ? 'hd_wrinkle' : 'wrinkle'
  );
  const [showPreview, setShowPreview] = useState(true);
  const [index, setIndex] = useState(
    !_isEmpty(recommendCategory) && recommendCategory[0].value
  );
  const [scoreFormat, setScoreFormat] = useState('percentile');
  const firstCategory = _get(transformedSequenceOfCategory, '[0]');
  const appLang = langCode.getAppLang();
  const [activeLang, setActiveLang] = useState(appLang || 'en');
  const [language, setLanguage] = useState(
    getDetectionLanguages().find(l => l.value === appLang) ||
      getDetectionLanguages()[0]
  );
  const [device, setDevice] = useState(
    deviceOptions
      .filter(o => o.value !== 'mobile')
      .find(
        option =>
          option.value === storeUtils.getUiConfigSettingDevice(customerId)
      ) || deviceOptions[0]
  );
  const [fullScreen, setFullScreen] = useState(false);
  const [customSkinConcernData, setCustomSkinConcernData] = useState({});

  const detectionMapping = useMemo(() => {
    const baseMapping = isHd
      ? {...detectionHdMap, ...skinTypeHdMap}
      : detectionMap;

    if (!customSkinConcernData) {
      return baseMapping;
    }

    const enhanced = {...baseMapping};

    Object.entries(customSkinConcernData).forEach(([lang, langData]) => {
      if (langData && typeof langData === 'object') {
        Object.entries(langData).forEach(([categoryKey, customLabel]) => {
          if (enhanced[categoryKey] && customLabel) {
            enhanced[categoryKey] = {
              ...enhanced[categoryKey],
              labelI18n: {
                ...enhanced[categoryKey].labelI18n,
                [lang]: customLabel,
              },
            };
          }
        });
      }
    });

    return enhanced;
  }, [isHd, customSkinConcernData]);

  const isLandscape = _get(device, 'value') === deviceOptions[1].value;
  const windowHeight = useWindowHeight();
  const windowWidth = useWindowWidth();

  function calculateTransform(windowWidth, windowHeight, isLandscape) {
    return `scale(${
      windowWidth > windowHeight
        ? isLandscape
          ? windowHeight * 0.0027
          : windowHeight * 0.0018
        : windowWidth * 0.0021
    })`;
  }

  useEffect(() => {
    if (!isShowSkinHealth) {
      setActiveDetection(transformedSequenceOfCategory[0].value);
    }
    if (isHd) {
      skincareProDao.getScoreFormat().then(r => {
        setScoreFormat(r.score_format);
      });
    }
  }, [firstCategory, isShowSkinHealth]);

  useEffect(() => {
    if (!isHd) return;
    
    const fetchAppSettings = async () => {
      try {
        const appSettingsConfig = await skincareProDao.getCustomerSetting({
          type: 'ScpAppSettings',
        });

        if (appSettingsConfig) {
          const customSkinConcern = _get(appSettingsConfig, 'editing_revision.data.custom_skin_concern_name', {});
          setCustomSkinConcernData(customSkinConcern);
        }
      } catch (error) {
        console.error('Error fetching app settings:', error);
      }
    };

    fetchAppSettings();
  }, [isHd]);

  useEffect(() => {
    if (_isEmpty(recommendCategory.filter(r => !r.disabled))) return;
    setIndex(recommendCategory.filter(r => !r.disabled)[0].value);
  }, [recommendCategory.filter(r => !r.disabled).length]);

  return (
    <div className="d-flex flex-column">
      <PreviewToolBar
        showPreview={showPreview}
        setShowPreview={setShowPreview}
        setActiveLang={setActiveLang}
        language={language}
        setLanguage={setLanguage}
        setIsZoomed={setFullScreen}
        device={device}
        setDevice={setDevice}
      />
      {showPreview && (
        <div
          className={classnames({
            'd-none': !showPreview,
          })}
          style={{
            marginTop: '20px',
          }}
        >
          <div
            className={classnames({
              'skincaredetectionpreview-model': true,
              'skincaredetectionpreview-model--v1': !isV2Above,
              'skincaredetectionpreview-model--landscape': isLandscape,
            })}
          >
            <DetectionPreviewModal
              type={_get(device, 'value')}
              commonData={commonData}
              isHd={isHd}
              activeDetection={activeDetection}
              setActiveDetection={setActiveDetection}
              transformedSequenceOfCategory={transformedSequenceOfCategory}
              detectionMapping={detectionMapping}
              isShowSkinAge={isShowSkinAge}
              language={language}
              skincareRecommendMode={skincareRecommendMode}
              isShowSkinType={isShowSkinType}
              isShowSkinHealth={isShowSkinHealth}
              scoreFormat={scoreFormat}
              activeLang={activeLang}
              recommendCategory={recommendCategory}
              detectionDescriptionContent={detectionDescriptionContent}
              index={index}
              setIndex={setIndex}
              buttonText={buttonText}
            />
          </div>
          <Modal
            size="sm"
            show={fullScreen}
            onHide={() => {
              setFullScreen(false);
            }}
            centered={true}
            dialogClassName="skincaredetectionpreview-fullmodal"
          >
            <Modal.Body className="skincaredetectionpreview-fullmodal__body">
              <div className="skincaredetectionpreview-fullmodal__body--container">
                <div className="skincaredetectionpreview-fullmodal__body--module-expand">
                  <div
                    className={classnames({
                      'skincaredetectionpreview-model': true,
                      'skincaredetectionpreview-model--landscape': isLandscape,
                    })}
                    style={{
                      transform: calculateTransform(
                        windowWidth,
                        windowHeight,
                        isLandscape
                      ),
                      transformOrigin: 'center center',
                    }}
                  >
                    <DetectionPreviewModal
                      type={_get(device, 'value')}
                      commonData={commonData}
                      isHd={isHd}
                      activeDetection={activeDetection}
                      setActiveDetection={setActiveDetection}
                      transformedSequenceOfCategory={
                        transformedSequenceOfCategory
                      }
                      detectionMapping={detectionMapping}
                      isShowSkinAge={isShowSkinAge}
                      language={language}
                      skincareRecommendMode={skincareRecommendMode}
                      isShowSkinType={isShowSkinType}
                      isShowSkinHealth={isShowSkinHealth}
                      scoreFormat={scoreFormat}
                      activeLang={activeLang}
                      recommendCategory={recommendCategory}
                      detectionDescriptionContent={detectionDescriptionContent}
                      index={index}
                      setIndex={setIndex}
                      buttonText={buttonText}
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        </div>
      )}
    </div>
  );
}
